/**
 * UbicacionMapaPicker — mapa Google con marcador draggable + botón "Ubicar por
 * dirección" que consulta el proxy backend de Geocoding.
 *
 * Fix del pin dentro de <Dialog> de MUI:
 *   El problema histórico era que cuando este picker se montaba dentro de un
 *   Dialog con coords YA presentes (caso detalle admin de aliados), el mapa
 *   medía su contenedor durante la animación de apertura del Dialog (~225ms),
 *   quedaba con dimensiones intermedias raras, y el <Marker> nunca aparecía
 *   porque se registraba contra un mapa sin bounding box correcto. En el
 *   wizard /aliarse funcionaba porque el usuario interactuaba (click/geocode)
 *   DESPUÉS de la animación, forzando el re-render.
 *
 * Solución:
 *   1. Se usa MarkerF/CircleF (variantes con React.forwardRef, más estables
 *      que Marker/Circle legacy en lifecycle dentro de Dialogs).
 *   2. En onLoad se captura la instancia y se registra addListenerOnce('idle')
 *      para marcar mapReady=true cuando el mapa emite su primer idle. El evento
 *      idle solo dispara cuando el mapa está 100% listo para overlays.
 *   3. El <MarkerF> se renderiza SOLO cuando mapReady=true, con key basada en
 *      lat/lng para forzar re-mount limpio.
 *   4. Un useEffect adicional dispara trigger('resize') + panTo con
 *      setTimeout(300ms) cuando cambian coords, para reforzar recentrado
 *      después de la animación de apertura del Dialog.
 *   5. gestureHandling siempre 'greedy' (no bloquear zoom con rueda en modo
 *      lectura; la protección contra mover el pin ya la da draggable={false}).
 *   6. Controles restaurados: mapTypeControl (Mapa/Satélite/Híbrido) y
 *      fullscreenControl activos.
 *   7. Se quitó styles con POIs ocultos → se ven conjuntos residenciales,
 *      comercios y todos los POIs como Google Maps normal.
 */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { GoogleMap, MarkerF, CircleF, useJsApiLoader } from '@react-google-maps/api';
import { Box, Typography, Button, Stack, Alert, CircularProgress } from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { geografiaService } from '../services/api';

// Fallback de coordenadas: centro Bogota
const DEFAULT_LAT = 4.7110;
const DEFAULT_LNG = -74.0721;

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';

const GOOGLE_MAPS_LIBRARIES = [];

const UbicacionMapaPicker = ({
  latitud,
  longitud,
  onCambio,
  radioKm = null,
  alto = 350,
  zoomInicial = 15,
  soloLectura = false,
  ayuda,
  direccionParaGeocode = null,
  ciudadParaGeocode = null,
  departamentoParaGeocode = null,
}) => {
  const lat = Number(latitud);
  const lng = Number(longitud);
  const hayCoordenadas = !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
  const posicion = useMemo(
    () => ({
      lat: hayCoordenadas ? lat : DEFAULT_LAT,
      lng: hayCoordenadas ? lng : DEFAULT_LNG,
    }),
    [hayCoordenadas, lat, lng]
  );

  const [geoLoading, setGeoLoading] = useState(false);
  const [geoInfo, setGeoInfo] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [mapReady, setMapReady] = useState(false);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
    language: 'es',
    region: 'CO',
  });

  // Fix del pin dentro de Dialog: forzar resize + recentrar cuando el mapa
  // está listo o cuando cambian las coords. El setTimeout supera la animación
  // de apertura del Dialog de MUI (225ms default).
  useEffect(() => {
    if (!mapInstance || !hayCoordenadas || !window.google?.maps?.event) return;
    const t = setTimeout(() => {
      window.google.maps.event.trigger(mapInstance, 'resize');
      mapInstance.panTo(posicion);
    }, 300);
    return () => clearTimeout(t);
  }, [mapInstance, hayCoordenadas, lat, lng, posicion]);

  const handleMapLoad = useCallback((map) => {
    setMapInstance(map);
    // Escuchar UNA vez el primer 'idle' del mapa: cuando emite idle esta 100%
    // listo para overlays. Recien entonces montamos el <MarkerF>.
    if (window.google?.maps?.event) {
      window.google.maps.event.addListenerOnce(map, 'idle', () => {
        setMapReady(true);
      });
    }
  }, []);

  const handleMapUnmount = useCallback(() => {
    setMapInstance(null);
    setMapReady(false);
  }, []);

  const puedeGeocodificar =
    !soloLectura && onCambio &&
    !!(direccionParaGeocode || ciudadParaGeocode) &&
    !!ciudadParaGeocode && !!departamentoParaGeocode;

  const geocodificar = useCallback(async () => {
    setGeoLoading(true);
    setGeoInfo(null);
    try {
      const r = await geografiaService.geocodificarDireccion(
        direccionParaGeocode || '',
        ciudadParaGeocode || '',
        departamentoParaGeocode || '',
        'Colombia',
      );
      if (r?.lat != null && r?.lng != null) {
        onCambio({ lat: Number(r.lat), lng: Number(r.lng) });
        setGeoInfo({
          severity: 'success',
          mensaje: 'Ubicación encontrada. Ajusta el marcador si es necesario.',
        });
      } else {
        const st = r?.status || 'UNKNOWN';
        const msgs = {
          ZERO_RESULTS:     'No se encontraron resultados. Ajusta el marcador manualmente en el mapa.',
          REQUEST_DENIED:   'Google rechazó la solicitud. La API key del backend no está autorizada para Geocoding.',
          OVER_QUERY_LIMIT: 'Cuota de Google Geocoding agotada. Revisa facturación en GCP.',
          BACKEND_ERROR:    r.httpStatus === 503
            ? 'Servicio de geocoding no configurado en el backend.'
            : `Backend respondió HTTP ${r.httpStatus}.`,
          FETCH_ERROR:      'No se pudo conectar al backend.',
        };
        setGeoInfo({
          severity: st === 'ZERO_RESULTS' ? 'warning' : 'error',
          mensaje: msgs[st] || `Sin resultado (${st}). Ajusta el marcador manualmente.`,
        });
      }
    } catch (e) {
      setGeoInfo({
        severity: 'error',
        mensaje: e.message?.includes('503')
          ? 'Servicio de ubicación no disponible en este momento. Ajusta el marcador manualmente.'
          : 'Error contactando el servicio de ubicación. Ajusta el marcador manualmente.',
      });
    } finally {
      setGeoLoading(false);
    }
  }, [direccionParaGeocode, ciudadParaGeocode, departamentoParaGeocode, onCambio]);

  const handleMarkerDragEnd = useCallback((e) => {
    if (soloLectura || !onCambio) return;
    onCambio({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  }, [onCambio, soloLectura]);

  const handleMapClick = useCallback((e) => {
    if (soloLectura || !onCambio) return;
    onCambio({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  }, [onCambio, soloLectura]);

  if (!GOOGLE_MAPS_KEY || GOOGLE_MAPS_KEY === 'XXXXXXX') {
    return (
      <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50' }}>
        <Alert severity="warning" variant="outlined">
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Mapa no disponible
          </Typography>
          <Typography variant="caption">
            La clave <code>REACT_APP_GOOGLE_MAPS_KEY</code> no está configurada.
            Agrégala a tu <code>.env</code> y reinicia el servidor de desarrollo.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {puedeGeocodificar && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={geoLoading ? <CircularProgress size={14} /> : <MyLocationIcon />}
            onClick={geocodificar}
            disabled={geoLoading}
          >
            Ubicar por dirección
          </Button>
          <Typography variant="caption" color="text.secondary">
            Usa los datos que ingresaste para posicionar el marcador
          </Typography>
        </Stack>
      )}

      {geoInfo && (
        <Alert severity={geoInfo.severity} sx={{ mb: 1, py: 0.3 }} onClose={() => setGeoInfo(null)}>
          <Typography variant="caption" sx={{ display: 'block' }}>{geoInfo.mensaje}</Typography>
        </Alert>
      )}

      <Box
        sx={{
          height: alto,
          width: '100%',
          borderRadius: 2,
          overflow: 'hidden',
          border: 1,
          borderColor: 'divider',
          position: 'relative',
        }}
      >
        {loadError && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">
              No se pudo cargar Google Maps. Verifica la clave API y sus restricciones en GCP Console.
            </Alert>
          </Box>
        )}
        {!isLoaded && !loadError && (
          <Box sx={{
            height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'grey.100',
          }}>
            <CircularProgress size={30} />
          </Box>
        )}
        {isLoaded && !loadError && (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={posicion}
            zoom={zoomInicial}
            onClick={handleMapClick}
            onLoad={handleMapLoad}
            onUnmount={handleMapUnmount}
            options={{
              streetViewControl: false,
              mapTypeControl: true,
              mapTypeControlOptions: {
                mapTypeIds: ['roadmap', 'satellite', 'hybrid'],
              },
              fullscreenControl: true,
              zoomControl: true,
              gestureHandling: 'greedy',
            }}
          >
            {mapReady && hayCoordenadas && (
              <MarkerF
                key={`mk-${lat}-${lng}`}
                position={posicion}
                draggable={!soloLectura}
                onDragEnd={handleMarkerDragEnd}
              />
            )}
            {mapReady && hayCoordenadas && radioKm != null && Number(radioKm) > 0 && (
              <CircleF
                key={`ci-${lat}-${lng}-${radioKm}`}
                center={posicion}
                radius={Number(radioKm) * 1000}
                options={{
                  fillColor: '#1976d2',
                  fillOpacity: 0.1,
                  strokeColor: '#1976d2',
                  strokeOpacity: 0.6,
                  strokeWeight: 2,
                  clickable: false,
                }}
              />
            )}
          </GoogleMap>
        )}
      </Box>

      {ayuda !== false && !soloLectura && isLoaded && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Arrastra el marcador o haz click en el mapa para elegir el punto exacto.
        </Typography>
      )}
      {hayCoordenadas && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
          {lat.toFixed(5)}, {lng.toFixed(5)}
        </Typography>
      )}
    </Box>
  );
};

export default UbicacionMapaPicker;

